import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Loader2, Search as SearchIcon, ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import WebDealCard from "./WebDealCard";

const DEMO_QUERIES = [
  "wireless earbuds under $100",
  "running shoes for wide feet",
  "kitchen knives set",
  "gaming chair with lumbar support",
  "espresso machine for beginners",
  "carry-on luggage TSA-approved",
];

export default function HomeWebSearchHero() {
  const [q, setQ] = useState("");
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState("");
  const [suggestIdx, setSuggestIdx] = useState(0);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Rotating placeholder suggestion
  useEffect(() => {
    const id = setInterval(() => setSuggestIdx((i) => (i + 1) % DEMO_QUERIES.length), 3500);
    return () => clearInterval(id);
  }, []);

  const runSearch = useCallback(async (query) => {
    const t = query.trim();
    if (t.length < 3) {
      setDeals([]);
      setLoading(false);
      setError("");
      setSource("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/search/web", { query: t, limit: 3 });
      setDeals(data.deals || []);
      setSource(data.source || "");
    } catch (err) {
      setError(err?.response?.data?.detail || "Web search unavailable");
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 650);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, runSearch]);

  const goFull = (e) => {
    e?.preventDefault();
    const t = q.trim();
    if (t) navigate(`/search?q=${encodeURIComponent(t)}`);
  };

  const tryDemo = (query) => {
    setQ(query);
  };

  return (
    <section className="border-y border-black bg-white" data-testid="home-web-search">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-12 gap-6 items-start">
          <div className="col-span-12 md:col-span-5">
            <div className="inline-flex items-center gap-2 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-[0.25em] px-2 py-1 mb-4">
              <Globe className="w-3 h-3" strokeWidth={2.5} /> Live · Powered by Google Search
            </div>
            <h2 className="font-serif font-black text-4xl md:text-5xl leading-[0.95] tracking-tighter mb-4">
              Ask for anything.<br /><span className="italic font-normal">We crawl the web live.</span>
            </h2>
            <p className="text-sm text-black/60 mb-5 leading-relaxed max-w-md">
              Type a product, brand, or category. DealHub queries the internet in real time and returns real merchant deals — save any find to your bookmarks with one click.
            </p>
            <form onSubmit={goFull} className="relative">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40" strokeWidth={2} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Try "${DEMO_QUERIES[suggestIdx]}"`}
                className="w-full pl-11 pr-32 py-4 border-2 border-black bg-white text-base font-serif focus:outline-none focus:ring-0 focus:border-[#E63946] rounded-none"
                data-testid="home-search-input"
                maxLength={140}
              />
              <button
                type="submit"
                disabled={q.trim().length < 3}
                className="absolute right-1 top-1 bottom-1 px-4 bg-black text-white text-xs font-bold uppercase tracking-widest hover:bg-[#E63946] disabled:opacity-40"
                data-testid="home-search-go"
              >
                See all <ArrowRight className="inline w-3 h-3 ml-1" strokeWidth={2.5} />
              </button>
            </form>
            <div className="flex flex-wrap gap-2 mt-4" data-testid="home-search-suggestions">
              <span className="text-[10px] font-mono uppercase tracking-widest text-black/40 mr-1 py-1.5">Try:</span>
              {DEMO_QUERIES.slice(0, 3).map((demo) => (
                <button
                  key={demo}
                  onClick={() => tryDemo(demo)}
                  className="text-[11px] px-2.5 py-1.5 border border-black/15 hover:border-black hover:bg-black hover:text-white font-medium"
                  data-testid={`home-search-demo-${demo.slice(0, 12)}`}
                >
                  {demo}
                </button>
              ))}
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            {q.trim().length < 3 ? (
              <div className="border-2 border-dashed border-black/15 h-full min-h-[240px] flex flex-col items-center justify-center p-8 text-center" data-testid="home-search-idle">
                <Sparkles className="w-6 h-6 text-black/25 mb-3" strokeWidth={1.5} />
                <div className="font-serif text-xl text-black/50">Type a query above</div>
                <p className="text-xs text-black/40 mt-2 max-w-xs">Live preview of real deals from around the web appears here as you type.</p>
              </div>
            ) : loading ? (
              <div className="border-2 border-dashed border-black/15 h-full min-h-[240px] flex flex-col items-center justify-center p-8 text-center" data-testid="home-search-loading">
                <Loader2 className="w-6 h-6 animate-spin text-black/40 mb-3" strokeWidth={2} />
                <div className="text-xs font-mono uppercase tracking-widest text-black/40">Crawling for &ldquo;{q.trim()}&rdquo;…</div>
              </div>
            ) : error ? (
              <div className="border-2 border-dashed border-[#E63946]/30 h-full min-h-[240px] flex items-center justify-center p-8 text-center text-sm text-[#E63946]" data-testid="home-search-error">
                {error}
              </div>
            ) : deals.length === 0 ? (
              <div className="border-2 border-dashed border-black/15 h-full min-h-[240px] flex items-center justify-center p-8 text-center text-sm text-black/50" data-testid="home-search-empty">
                No live results yet. Try a different query.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-black/50">
                    Live web preview {source === "cache" && "· cached"}
                  </div>
                  <button onClick={goFull} className="text-[11px] font-bold uppercase tracking-widest hover:text-[#E63946]" data-testid="home-search-see-all">
                    See all results <ArrowRight className="inline w-3 h-3" strokeWidth={2} />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="home-search-preview-grid">
                  {deals.map((d) => <WebDealCard key={d.id} deal={d} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
